/**
 * Imperative bridge so tab close / logout can destroy in-memory KeepAlive panes
 * without coupling Layout to InternalTabs.
 * State lives in RAM only — never localStorage/sessionStorage.
 */
type KeepAliveBridge = {
  destroyByTabId: (tabId: string) => void
  destroyMatching: (match: string | RegExp) => void
  destroyAll: () => void
}

let bridge: KeepAliveBridge | null = null

export function registerKeepAliveBridge(next: KeepAliveBridge | null) {
  bridge = next
}

export function destroyKeepAliveForTab(tabId: string) {
  bridge?.destroyByTabId(tabId)
}

export function destroyKeepAliveForTabs(tabIds: string[]) {
  tabIds.forEach((id) => bridge?.destroyByTabId(id))
}

/** Destroy panes whose cache key matches a substring or RegExp (e.g. `/invoices/new`). */
export function destroyKeepAliveMatching(match: string | RegExp) {
  bridge?.destroyMatching(match)
}

/** Fresh new sales invoice — drop any cached `/invoices/new` pane so line items start empty. */
export function destroyNewSalesInvoiceKeepAlive() {
  destroyKeepAliveMatching(/::\/invoices\/new(\?|$|::)/)
}

/** Fresh opening-stock form — drop any cached `/opening-stock-invoices/new` pane so lines start empty. */
export function destroyNewOpeningStockKeepAlive() {
  destroyKeepAliveMatching(/::\/opening-stock-invoices\/new(\?|$|::)/)
}

export function destroyAllKeepAlive() {
  bridge?.destroyAll()
}

/**
 * Cache key for a tab + route.
 * Search params are omitted on purpose — filters/pagination must not create extra RAM panes.
 */
export function keepAliveCacheKey(tabId: string, pathname: string) {
  return `${tabId}::${pathname}`
}

export function keepAliveTabIdFromCacheKey(cacheKey: string): string {
  const sep = cacheKey.indexOf('::')
  return sep >= 0 ? cacheKey.slice(0, sep) : cacheKey
}
